import { useState } from "react";

import {
  Grid,
  Box,
  FormControl,
  MenuItem,
  Dialog,
  DialogContent,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Tooltip,
} from "@mui/material";

import { DateTime, Info, Duration } from "luxon";

import { constant_names } from "../../utils/constants";
import DayButton from "./DayButton";
import DialogHeader from "./DialogHeader";
import LitcoTextField from "./LitcoTextField";

const today = DateTime.now();
const thisYear = today.year;

const generateYearsOptions = (yearsBack, selectedYear, yearChangeHandler) => {
  const yearsOptions = [];
  const firstYear = thisYear - yearsBack;

  if (yearsBack <= 3) {
    for (let i = firstYear; i <= thisYear; i++) {
      yearsOptions.push(<FormControlLabel value={i} key={i} control={<Radio />} label={i} />);
    }

    return (
      <FormControl>
        <FormLabel id="yearLabel">Year</FormLabel>
        <RadioGroup row aria-labelledby="yearLabel" name="yearGroup" value={selectedYear} onChange={yearChangeHandler}>
          {yearsOptions}
        </RadioGroup>
      </FormControl>
    );
  }

  if (yearsBack > 3) {
    for (let i = firstYear; i <= thisYear; i++) {
      yearsOptions.push(
        <MenuItem value={i} key={i}>
          {i}
        </MenuItem>
      );
    }

    return (
      <Grid item sm={6}>
        <LitcoTextField
          label="Year"
          labelStyle={{ color: "#949494", fontSize: 21, lineHeight: 1 }}
          select={yearsOptions}
          value={selectedYear}
          onChange={yearChangeHandler}
        />
      </Grid>
    );
  }
};

const generateMonthOptions = () => {
  const months = Info.months();
  const monthOptions = [];

  months.forEach((month, i) => {
    monthOptions.push(
      <MenuItem value={i + 1} key={i}>
        {month}
      </MenuItem>
    );
  });

  return monthOptions;
};

const generateCalendarButtons = (selectedMonth, yearsBack) => {
  const firstDayWeekday = selectedMonth.weekday;
  const calendarButtons = { Sun: [], Mon: [], Tue: [], Wed: [], Thu: [], Fri: [], Sat: [] };
  const yearsAgo = today.minus(Duration.fromObject({ years: yearsBack }));

  if (firstDayWeekday !== 7) {
    for (let i = 1; i <= firstDayWeekday; i++) {
      calendarButtons[constant_names.WEEKDAYS_NAMES[i]].push(0);
    }
  }

  for (let i = 1; i <= selectedMonth.daysInMonth; i++) {
    const day = DateTime.fromObject({
      year: selectedMonth.year,
      month: selectedMonth.month,
      day: i,
    });

    const weekday = day.weekdayShort;
    const dayDisabled = day > today || day < yearsAgo;
    const buttonTooltip = day < yearsAgo;

    calendarButtons[weekday].push({ day: i, dayDisabled, buttonTooltip });
  }

  return calendarButtons;
};

export default function DatePicker(props) {
  const { open, onClose, onDateSelected, title, yearsBack, tooltipText } = props;
  const selectedDate = props.selectedDate ? DateTime.fromISO(props.selectedDate) : today;

  const [year, setYear] = useState(selectedDate.year);
  const [month, setMonth] = useState(selectedDate.month);

  const selectedMonth = DateTime.fromObject({ year, month });
  const calendarButtons = generateCalendarButtons(selectedMonth, yearsBack);

  const yearChangeHandler = (event) => {
    setYear(event.target.value);
  };

  const monthChangeHandler = (event) => {
    setMonth(event.target.value);
  };

  const dayChangeHandler = (event) => {
    const day = +event.target.value;
    const newSelectedDate = DateTime.fromObject({ year, month, day });
    onDateSelected(newSelectedDate);
    onClose();
  };

  return (
    <Dialog onClose={onClose} open={open}>
      <DialogHeader title={title} onClose={onClose} />
      <DialogContent>
        <Grid container spacing={2} mt={1}>
          <Grid item xs={12} sm={6} md={8}>
            {generateYearsOptions(yearsBack, year, yearChangeHandler)}
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <LitcoTextField
              label="Month"
              labelStyle={{ color: "#949494", fontSize: 21, lineHeight: 1 }}
              select={generateMonthOptions()}
              value={month}
              onChange={monthChangeHandler}
            />
          </Grid>

          <Grid item xs={12}>
            <Grid container justifyContent="space-between">
              {Object.keys(calendarButtons).map((weekDay) => (
                <Grid item key={weekDay}>
                  <Grid container direction="column" alignItems="center" spacing={1}>
                    <Grid item>
                      {weekDay}
                    </Grid>
                    {calendarButtons[weekDay].map((day, i) => {
                      return (
                        <Grid item key={i}>
                          {day === 0 ? (
                            <Box minHeight={35}></Box>
                          ) : day.buttonTooltip && tooltipText ? (
                            <Tooltip title={tooltipText}>
                              <span>
                                <DayButton day={day} onClick={dayChangeHandler} />
                              </span>
                            </Tooltip>
                          ) : (
                            <DayButton day={day} onClick={dayChangeHandler} />
                          )}
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
}
